import Icon from "@expo/vector-icons/MaterialCommunityIcons";
/* -----------------UI--------------- */
import {
	Appbar,
	IconButton,
	Menu,
	ProgressBar,
	Text,
} from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import useSessionStore from "../hooks/useSessionStore";

/* -----------------Child components--------------- */
import Box from "@memorang/ui/src/components/Box";
import Container from "@memorang/ui/src/components/Container";

/* -----------------Others--------------- */
import { useAppTheme } from "@hooks/useAppTheme";

import { useDevice } from "@hooks/useDevice";
import { useLocalSearchParams, usePathname } from "expo-router";
import { memo, useState } from "react";
import isEqual from "react-fast-compare";
import type { BlockState } from "../hooks/types";
import useCurrentSession from "../hooks/useCurrentSession";
type Props = {
	showClose?: boolean;
	handleAction?: () => void;
	showMenuIcon?: boolean;
	handleClickMenuItem?: (type: "pause" | "help") => void;
	isAnswered?: boolean;
	fullWidth?: boolean;
	sessionType?: string;
	timeRemaining?: string;
};

const RenderBlocks = ({
	blocks,
	titleText,
	blockProgress,
	inReview,
	isMobile,
	timeRemaining,
}: {
	blocks: BlockState[];
	titleText: string;
	blockProgress: string;
	inReview?: boolean;
	isMobile?: boolean;
	timeRemaining?: string;
}) => {
	const theme = useAppTheme();

	return (
		<Box flex={1} alignItems="center" gap={8}>
			{isMobile ? (
				<Text
					variant={"titleMedium"}
					style={{
						paddingBottom: 4,
						color: theme.colors.white,
					}}
				>
					{timeRemaining}
				</Text>
			) : (
				<Text
					variant={"titleLarge"}
					style={{
						color: theme.colors.white,
					}}
				>
					{titleText}
				</Text>
			)}

			<Box flexDirection="row" gap={8} width="100%">
				{blocks.map(({ currentProgress }, index) => (
					<Box key={index.toString()} flex={1}>
						<ProgressBar
							progress={currentProgress || 0}
							color={theme.colors.info.main}
							aria-label="Progress bar"
							style={{
								height: 8,
								borderRadius: 4,
								width: null,
								backgroundColor: theme.colors.white,
							}}
						/>
					</Box>
				))}
			</Box>
			{!inReview && blocks.length > 1 ? (
				<Text
					variant={"labelLarge"}
					style={{
						color: theme.colors.white,
					}}
				>
					{blockProgress}
				</Text>
			) : (
				<Box width={27} height={27} />
			)}
		</Box>
	);
};

const StudySessionProgressBar = ({
	sessionType,
	showClose,
	handleAction,
	showMenuIcon,
	handleClickMenuItem,
	isAnswered,
	fullWidth,
	timeRemaining,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const theme = useAppTheme();

	const pathname = usePathname();
	const isReportPage = pathname.includes("report");
	const currentProgress = useSessionStore((state) => state.currentProgress);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const [menuVisible, setMenuVisible] = useState(false);
	const blockStates = useSessionStore((state) => state.blockStates);

	const { currentBlockIndex, numSessionItems, currentBlock } =
		useCurrentSession();

	const { isMobile } = useDevice();
	const { index } = useLocalSearchParams<{
		index: string;
	}>();
	const currentItemIndex = index ? Number.parseInt(index) : 0;
	const titleText = `${currentItemIndex} of ${currentBlock.length}`;
	const blocks = [...blockStates.values()];
	const blockProgress = `Section ${currentBlockIndex + 1} of ${
		numSessionItems
	}`;

	const pauseActionIcon = inReviewMode
		? "check-circle-outline"
		: "pause-circle-outline";
	const pauseActionLabel = inReviewMode ? "End Review" : "Pause Session";

	const EPSILON = Number.EPSILON * 100;
	const isProgressComplete = isReportPage
		? sessionType === "SUMMATIVE"
			? true
			: Math.abs(1 - currentProgress) <= EPSILON
		: Math.abs(1 - currentProgress) <= EPSILON && isAnswered;
	const progressBoxBackgroundColor = isProgressComplete
		? theme.colors.primary
		: theme.colors.surfaceVariant;

	return (
		<Container maxWidth={fullWidth ? "lg" : "md"}>
			<Box
				flexDirection="row"
				alignItems="center"
				paddingRight={showMenuIcon ? 0 : 8}
				paddingLeft={showClose ? 0 : 8}
			>
				{showClose && (
					<IconButton
						onPress={handleAction}
						iconColor={
							isSummativeTest
								? theme.colors.white
								: theme.colors.onSurfaceVariant
						}
						icon={(props) => <Icon {...props} name="close" />}
					/>
				)}
				{isSummativeTest ? (
					<RenderBlocks
						blocks={blocks}
						titleText={titleText}
						blockProgress={blockProgress}
						inReview={inReviewMode}
						isMobile={isMobile}
						timeRemaining={timeRemaining}
					/>
				) : (
					<Box flex={1} position="relative">
						<ProgressBar
							aria-label="Progress bar"
							progress={
								isReportPage && sessionType === "SUMMATIVE"
									? 1
									: currentProgress
							}
							style={{
								height: 8,
								borderRadius: 4,
							}}
						/>
					</Box>
				)}

				{showMenuIcon && (
					<Menu
						visible={menuVisible}
						onDismiss={() => setMenuVisible(false)}
						anchor={
							<Appbar.Action
								color={
									isSummativeTest
										? theme.colors.white
										: theme.colors.onSurfaceVariant
								}
								icon={"dots-vertical"}
								onPress={() => setMenuVisible(true)}
							/>
						}
					>
						<Menu.Item
							leadingIcon={pauseActionIcon}
							title={pauseActionLabel}
							onPress={() => handleClickMenuItem?.("pause")}
						/>
						<Menu.Item
							leadingIcon={"help-circle-outline"}
							title={"Help"}
							onPress={() => handleClickMenuItem?.("help")}
						/>
					</Menu>
				)}

				{!isSummativeTest && (
					<Box
						backgroundColor={progressBoxBackgroundColor}
						width={44}
						height={44}
						borderRadius={22}
						justifyContent="center"
						alignItems="center"
						style={{
							marginLeft: -3,
							zIndex: 1000,
						}}
					>
						<Icon size={24} name={"check"} color={theme.colors.white} />
					</Box>
				)}
			</Box>
		</Container>
	);
};

export default memo(StudySessionProgressBar, isEqual);
